import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type { Notification } from "@/types/notification";

type NotificationRow = Record<string, unknown>;
const SOURCE_STEP_KEY_APPOINTMENT_TYPES = new Set(["appointment", "guest_appointment"]);
const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Notification Service — CRUD for notifications
 *
 * SCHEMA: notifications hat KEIN practice_id und KEIN is_read!
 * Spalten: id, user_id, title, body, notification_type, priority, status, read_at, created_at
 * Filter: user_id (nicht practice_id), read_at IS NULL für unread
 */

/** Fetch recent notifications for a user */
export async function getNotifications(
    userId: string,
    options?: { limit?: number; unreadOnly?: boolean },
): Promise<Notification[]> {
    if (!isSupabaseConfigured()) return [];

    try {
        let query = supabase
            .from("notifications")
            .select("*")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(options?.limit ?? 30);

        if (options?.unreadOnly) {
            query = query.is("read_at", null);
        }

        const { data, error } = await query;
        if (error) throw error;
        const rows = data || [];
        const validSourceStepKeyRefs = await resolveExistingAppointmentRefs(rows);
        return rows.map((row) => mapNotification(row, validSourceStepKeyRefs));
    } catch (err) {
        logger.error("Failed to fetch notifications", err);
        return [];
    }
}

/** Get count of unread notifications */
export async function getUnreadCount(userId: string): Promise<number> {
    if (!isSupabaseConfigured()) return 0;

    try {
        const { count, error } = await supabase
            .from("notifications")
            .select("id", { count: "exact" })
            .eq("user_id", userId)
            .is("read_at", null)
            .limit(1);

        if (error) throw error;
        return count ?? 0;
    } catch (err) {
        logger.error("Failed to fetch unread count", err);
        return 0;
    }
}

/** Mark single notification as read */
export async function markAsRead(notificationId: string): Promise<boolean> {
    if (!isSupabaseConfigured()) return false;

    try {
        const { error } = await supabase
            .from("notifications")
            .update({ read_at: new Date().toISOString() })
            .eq("id", notificationId);

        if (error) throw error;
        return true;
    } catch (err) {
        logger.error("Failed to mark notification as read", err);
        return false;
    }
}

/** Mark all notifications as read for a user */
export async function markAllAsRead(userId: string): Promise<boolean> {
    if (!isSupabaseConfigured()) return false;

    try {
        const { error } = await supabase
            .from("notifications")
            .update({ read_at: new Date().toISOString() })
            .eq("user_id", userId)
            .is("read_at", null);

        if (error) throw error;
        return true;
    } catch (err) {
        logger.error("Failed to mark all notifications as read", err);
        return false;
    }
}

function mapNotification(row: NotificationRow, validSourceStepKeyRefs: Set<string>): Notification {
    const appointmentRequestPreview = extractAppointmentRequestPreview(row);
    const resolvedAction = resolveNotificationAction(row, appointmentRequestPreview, validSourceStepKeyRefs);

    return {
        id: asString(row.id),
        practiceId: asString(row.user_id),
        type: normalizeNotificationType(row.notification_type ?? row.type),
        title: asString(row.title),
        body: asString(row.body),
        isRead: row.read_at != null,
        actionUrl: resolvedAction.actionUrl,
        usesCalendarFallback: resolvedAction.usesCalendarFallback,
        appointmentRequestPreview,
        createdAt: asString(row.created_at),
    };
}

function normalizeNotificationType(value: unknown): Notification["type"] {
    const rawType = asString(value).toLowerCase();

    if (rawType.startsWith("appointment")) return "appointment";
    if (rawType.startsWith("ticket") || rawType.includes("task")) return "ticket";
    if (rawType.startsWith("message") || rawType.includes("chat")) return "message";
    if (rawType.startsWith("invoice") || rawType.includes("billing")) return "invoice";
    if (rawType.startsWith("ai_phone") || rawType.includes("phone")) return "ai_phone";
    if (rawType.startsWith("referral") || rawType === "new_referral") return "referral";

    return "system";
}

function resolveNotificationAction(
    row: NotificationRow,
    appointmentRequestPreview: Notification["appointmentRequestPreview"],
    validSourceStepKeyRefs: Set<string>,
): {
    actionUrl?: string;
    usesCalendarFallback: boolean;
} {
    const directActionUrl = asNonEmptyString(row.action_url);
    if (directActionUrl) {
        return {
            actionUrl: directActionUrl,
            usesCalendarFallback: false,
        };
    }

    const actionType = asNonEmptyString(row.action_type);
    const actionData = asRecord(row.action_data) ?? asRecord(row.data);

    if (actionType === "navigate") {
        const screen = asNonEmptyString(actionData?.screen);
        const params = asRecord(actionData?.params);
        const appointmentId = asNonEmptyString(params?.appointmentId);

        if (
            appointmentId &&
            (screen === "AppointmentDetail" || screen === "GuestAppointmentDetail")
        ) {
            return {
                actionUrl: `/app/calendar?appointmentId=${encodeURIComponent(appointmentId)}`,
                usesCalendarFallback: false,
            };
        }
    }

    const sourceStepKeyAppointmentRef = extractAppointmentRefFromSourceStepKey(row);
    const validatedSourceStepKeyRef =
        sourceStepKeyAppointmentRef && validSourceStepKeyRefs.has(getAppointmentRefKey(sourceStepKeyAppointmentRef))
            ? sourceStepKeyAppointmentRef
            : undefined;
    const sourceType = asNonEmptyString(row.source_type) ?? validatedSourceStepKeyRef?.type;
    const sourceId = asNonEmptyString(row.source_id) ?? validatedSourceStepKeyRef?.id;

    // Referral deep-link: /app/referrals?referralId=<id>
    if (sourceId && sourceType === "referral") {
        return {
            actionUrl: `/app/referrals?referralId=${encodeURIComponent(sourceId)}`,
            usesCalendarFallback: false,
        };
    }

    if (sourceId && (sourceType === "appointment" || sourceType === "guest_appointment")) {
        return {
            actionUrl: `/app/calendar?appointmentId=${encodeURIComponent(sourceId)}`,
            usesCalendarFallback: false,
        };
    }

    if (appointmentRequestPreview) {
        const params = new URLSearchParams();
        params.set("date", appointmentRequestPreview.requestedDate);
        params.set("pendingNotificationId", asString(row.id));

        return {
            actionUrl: `/app/calendar?${params.toString()}`,
            usesCalendarFallback: true,
        };
    }

    return {
        actionUrl: undefined,
        usesCalendarFallback: false,
    };
}

async function resolveExistingAppointmentRefs(rows: NotificationRow[]): Promise<Set<string>> {
    const refs = rows
        .map((row) => extractAppointmentRefFromSourceStepKey(row))
        .filter((value): value is { type: "appointment" | "guest_appointment"; id: string } => Boolean(value));

    if (refs.length === 0) {
        return new Set<string>();
    }

    const appointmentIds = Array.from(
        new Set(refs.filter((ref) => ref.type === "appointment").map((ref) => ref.id)),
    );
    const guestAppointmentIds = Array.from(
        new Set(refs.filter((ref) => ref.type === "guest_appointment").map((ref) => ref.id)),
    );

    const validRefs = new Set<string>();

    if (appointmentIds.length > 0) {
        const { data, error } = await supabase.from("appointments").select("id").in("id", appointmentIds);
        if (!error) {
            for (const row of data || []) {
                const id = asNonEmptyString((row as Record<string, unknown>).id);
                if (id) {
                    validRefs.add(getAppointmentRefKey({ type: "appointment", id }));
                }
            }
        }
    }

    if (guestAppointmentIds.length > 0) {
        const { data, error } = await supabase
            .from("guest_appointments")
            .select("id")
            .in("id", guestAppointmentIds);
        if (!error) {
            for (const row of data || []) {
                const id = asNonEmptyString((row as Record<string, unknown>).id);
                if (id) {
                    validRefs.add(getAppointmentRefKey({ type: "guest_appointment", id }));
                }
            }
        }
    }

    return validRefs;
}

function extractAppointmentRefFromSourceStepKey(
    row: NotificationRow,
): { type: "appointment" | "guest_appointment"; id: string } | undefined {
    const sourceStepKey = asString(row.source_step_key).trim();
    if (!sourceStepKey) {
        return undefined;
    }

    const segments = sourceStepKey.split(":");
    const [entityType, , , sourceId] = segments;

    if (!entityType || !SOURCE_STEP_KEY_APPOINTMENT_TYPES.has(entityType)) {
        return undefined;
    }

    if (!sourceId || !UUID_PATTERN.test(sourceId)) {
        return undefined;
    }

    return {
        type: entityType as "appointment" | "guest_appointment",
        id: sourceId,
    };
}

function getAppointmentRefKey(ref: { type: "appointment" | "guest_appointment"; id: string }): string {
    return `${ref.type}:${ref.id}`;
}

function extractAppointmentRequestPreview(
    row: NotificationRow,
): Notification["appointmentRequestPreview"] | undefined {
    const title = asString(row.title);
    const body = asString(row.body);
    const sourceStepKey = asString(row.source_step_key);

    if (!title.toLowerCase().includes("terminanfrage") && !body.includes("möchte einen Termin am")) {
        return undefined;
    }

    const scheduleMatch = body.match(/am\s+(\d{2})\.(\d{2})\.(\d{4})\s+um\s+(\d{2}:\d{2})\s+Uhr/i);
    if (!scheduleMatch) {
        return undefined;
    }

    const [, day, month, year, time = "00:00"] = scheduleMatch;
    const requestedDate = `${year}-${month}-${day}`;
    const guestNameMatch = body.match(/Gast-Patient\s+(.+?)\s+\(Tel:/i);
    const patientNameMatch = body.match(/Patient\s+(.+?)\s+möchte einen Termin am/i);
    const patientName = guestNameMatch?.[1]?.trim() || patientNameMatch?.[1]?.trim() || "Anfrage";
    const isGuest =
        title.includes("(Gast)") ||
        body.startsWith("Gast-Patient") ||
        sourceStepKey.startsWith("guest_appointment:");

    return {
        patientName,
        requestedDate,
        requestedTime: time,
        isGuest,
    };
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function asString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function asNonEmptyString(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}
